import type {
  AiSkillHubSkillDetailData,
  AiSkillHubSkillDetailVersionHistoryItem,
  AiSkillHubSkillDownloadData,
  AiSkillHubSkillListItem,
} from '#/utils/http/types';

export type SkillItem = {
  id: string;
  name: string;
  description: string;
  summary: string;
  company: string;
  categories: string[];
  industries: string[];
  developerContactEmail: string;
  sourceType: number;
};

export type SkillDetailVersionHistoryItem = {
  versionSemver: string;
  publishDate: string;
  publishNotes: string;
  isActive: boolean;
};

export type SkillDetailItem = {
  skillId: string;
  name: string;
  description: string;
  sourceType: number;
  installableIn1PAgent: boolean;
  currentVersionSemver: string;
  categories: string[];
  industries: string[];
  countries: string[];
  authorizationRequirements?: string;
  screencastVideoLink?: string;
  examplePrompts: string[];
  developerContactEmail: string;
  legalDisclaimer?: string;
  versionHistory: SkillDetailVersionHistoryItem[];
  showcasePrompts?: string[];
};

export type SkillDownloadItem = {
  id: string;
  skillId: string;
  versionId: string;
  versionSemver: string;
  assetId: string;
  fileName: string;
  sizeBytes: string;
  contentType: string;
  downloadUrl?: string;
};

export const SKILLS_GRID_PAGE_SIZE = 9;
export const SKILLS_LIST_PAGE_SIZE = 10;
export const SKILLS_MOBILE_BATCH_SIZE = 4;

function truncateDescription(description: string, maxChars: number) {
  const normalizedDescription = description.trim();
  if (normalizedDescription.length <= maxChars) {
    return normalizedDescription;
  }
  return `${normalizedDescription.slice(0, maxChars).trimEnd()}...`;
}

export function normalizeCdnUrl(url?: string | null) {
  if (!url) {
    return undefined;
  }

  return url
    .replace('lf19-adcdn-sg.ibytedtos.com', 'sf-static.tiktokcdn.com')
    .replace('lf16-adcdn-sg.ibytedtos.com', 'sf-static.tiktokcdn.com');
}

function mapVersionHistoryItem(
  item: AiSkillHubSkillDetailVersionHistoryItem
): SkillDetailVersionHistoryItem {
  return {
    versionSemver: item.versionSemver || '',
    publishDate: item.publishDate || '',
    publishNotes: item.publishNotes || '',
    isActive: Boolean(item.isActive),
  };
}

export function mapAiSkillHubSkill(
  item: AiSkillHubSkillListItem,
  maxDescriptionChars = 200
): SkillItem {
  const resolvedDescription = (item.description || item.shortDescription || '').trim();

  return {
    id: item.skillId,
    name: item.name,
    description: resolvedDescription,
    summary: truncateDescription(resolvedDescription, maxDescriptionChars),
    company: item.company || '',
    categories: item.categories || [],
    industries: item.industries || [],
    developerContactEmail: item.developerContactEmail,
    sourceType: item.sourceType,
  };
}

export function mapAiSkillHubSkillDetail(
  item?: AiSkillHubSkillDetailData | null
): SkillDetailItem | null {
  if (!item) {
    return null;
  }

  const detailName = item.name?.trim();
  const detailSkillId = item.skillId?.trim();

  if (!detailName || !detailSkillId) {
    return null;
  }

  return {
    skillId: detailSkillId,
    name: detailName,
    description: (item.description || '').trim(),
    sourceType: item.sourceType,
    installableIn1PAgent: Boolean(item.installableIn1PAgent),
    currentVersionSemver: item.currentVersionSemver || '',
    categories: item.categories || [],
    industries: item.industries || [],
    countries: item.countries || [],
    authorizationRequirements: item.authorizationRequirements || undefined,
    screencastVideoLink: normalizeCdnUrl(item.screencastVideoLink),
    examplePrompts: item.examplePrompts || [],
    developerContactEmail: item.developerContactEmail || '',
    legalDisclaimer: item.legalDisclaimer || undefined,
    versionHistory: (item.versionHistory || []).map(mapVersionHistoryItem),
  };
}

type AiSkillHubSkillDownloadResponse = AiSkillHubSkillDownloadData & {
  skill_id?: string;
  version_id?: string;
  version_semver?: string;
  asset_id?: string;
  file_name?: string;
  size_bytes?: string;
  content_type?: string;
  download_url?: string;
};

export function mapAiSkillHubSkillDownload(
  item?: AiSkillHubSkillDownloadResponse | null
): SkillDownloadItem | null {
  if (!item) {
    return null;
  }

  return {
    id: item.id || '',
    skillId: item.skillId || item.skill_id || '',
    versionId: item.versionId || item.version_id || '',
    versionSemver: item.versionSemver || item.version_semver || '',
    assetId: item.assetId || item.asset_id || '',
    fileName: item.fileName || item.file_name || '',
    sizeBytes: item.sizeBytes || item.size_bytes || '',
    contentType: item.contentType || item.content_type || '',
    downloadUrl: normalizeCdnUrl(item.downloadUrl || item.download_url),
  };
}
